import { el } from '@/ui/dom';
import { googleSignInButton, injectGoogleSignInStyles } from './GoogleSignInButton';
import type { CloudSession } from './Session';

/**
 * The sign-in wall shown when a deployment refuses anonymous use.
 *
 * Mounted *instead of* the application, not over it. That distinction is the whole point: an
 * overlay would mean the editor, the renderer, the WebGL context and the local IndexedDB stores had
 * all been constructed and were merely hidden — a gate you can get behind with the element
 * inspector is not a gate. Here nothing beyond this screen exists until a session does.
 *
 * It is deliberately plain. There is exactly one thing to do, and the page has no navigation, no
 * marketing, and no second path — anything else would be an invitation to look for a way around.
 */

export interface SignInScreenDeps {
  container: HTMLElement;
  session: CloudSession;
  /**
   * The `?auth=` value the OAuth callback came back with, if any.
   *
   * `cancelled` is the one the server sends by name; anything else is reported in general terms
   * rather than ignored, so a redirect this screen has not been taught about still says something
   * true instead of looking like a sign-in that silently did nothing.
   */
  outcome?: string | null;
}

export class SignInScreen {
  readonly root: HTMLElement;

  constructor(private readonly deps: SignInScreenDeps) {
    injectGoogleSignInStyles();

    const button = googleSignInButton({
      size: 'large',
      block: true,
      // Return to whatever deep link brought them here, not to the root.
      onSignIn: () => this.deps.session.signIn(globalThis.location?.pathname ?? '/'),
    });

    const notice = describeOutcome(deps.outcome ?? null);

    this.root = el(
      'main',
      { class: 'lg-gate' },
      el(
        'div',
        { class: 'lg-gate__panel' },
        el('h1', { class: 'lg-gate__title', text: 'Lush Glade' }),
        el('p', {
          class: 'lg-gate__blurb',
          text: 'Design a garden in three dimensions — plant it, grow it, and watch it through the seasons.',
        }),
        notice === null
          ? null
          : el('p', { class: 'lg-gate__notice', attrs: { role: 'status' }, text: notice }),
        // The single action, and the only element on the screen with a white surface. Everything
        // above it is the reason to press it and everything below is the small print.
        el('div', { class: 'lg-gate__action' }, button),
        el('p', {
          class: 'lg-gate__fine',
          text:
            'Your sites are kept in your account and open on any machine you sign in on, with a ' +
            'monthly allowance of AI renders included.',
        }),
      ),
    );

    deps.container.appendChild(this.root);
  }

  dispose(): void {
    this.root.remove();
  }
}

/**
 * Turn the `?auth=` value into a sentence, or null when there is nothing to say.
 *
 * Unknown values are not dropped. The callback can only redirect here after something happened, so
 * a value this function has not seen still means the attempt ended early — saying so in general
 * terms beats a screen that looks identical to a first visit.
 */
export function describeOutcome(outcome: string | null): string | null {
  if (outcome === null || outcome === '') return null;
  if (outcome === 'cancelled') {
    return 'Sign-in was cancelled. Nothing was saved, and you can try again.';
  }
  return 'Sign-in did not complete. Nothing was saved, and you can try again.';
}

/**
 * Styles for the gate, injected rather than added to `hud.css`.
 *
 * The gate renders before the application exists, and therefore before anything that would normally
 * pull the HUD stylesheet in. Keeping it self-contained means the sign-in page cannot end up
 * unstyled because of load order — which on a wall like this would look like a broken deployment.
 */
export const SIGN_IN_STYLES = `
.lg-gate {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: radial-gradient(120% 100% at 50% 0%, #2b3f2c 0%, #16201a 55%, #0e1512 100%);
  color: #e8efe6;
  font: 15px/1.55 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}

.lg-gate__panel {
  width: min(30rem, 100%);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.lg-gate__title {
  margin: 0;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.lg-gate__blurb {
  margin: 0;
  max-width: 26rem;
  color: #b6c6b4;
}

.lg-gate__notice {
  margin: 0;
  padding: 9px 14px;
  border-radius: 8px;
  background: rgba(233, 196, 106, 0.12);
  border: 1px solid rgba(233, 196, 106, 0.3);
  color: #e9c46a;
  font-size: 13px;
}

/*
 * The button itself is .lg-gsi, from GoogleSignInButton.ts, and this sets none of its colours,
 * radius or type — the branding guidelines own those. All the gate contributes is the space around
 * it and how wide it is allowed to grow.
 */
.lg-gate__action {
  width: min(20rem, 100%);
  margin-top: 10px;
}

.lg-gate__action .lg-gsi:focus-visible {
  outline-color: #cfe3ff;
}

.lg-gate__fine {
  margin: 4px 0 0;
  font-size: 13px;
  color: #8fa38d;
}
`;
